import React, { useState, useEffect, useCallback } from 'react';
import { Button, Panel } from '../../design/primitives';
import { COLORS, SPACING, TYPOGRAPHY } from '../../design/tokens';
import { apiClient } from '../../api/apiConfig';
import { GroupedBudgetResponse } from '../../types/finance';
import { Spinner } from '../ui';
import MonthSelector from './MonthSelector';
import BudgetGroupPanel from './BudgetGroupPanel';

const formatCurrency = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

const BudgetDashboard: React.FC = () => {
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [data, setData] = useState<GroupedBudgetResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(false);

  const monthStr = `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, '0')}-01`;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/api/budgets/grouped/?month=${monthStr}`);
      setData(res.data);
    } catch (err) {
      console.error('Failed to fetch grouped budget', err);
    } finally {
      setLoading(false);
    }
  }, [monthStr]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleInitialize = async () => {
    setInitializing(true);
    try {
      await apiClient.post('/api/budgets/initialize_month/', { month: monthStr });
      await apiClient.post('/api/budgets/recalculate_spending/', { month: monthStr });
      await fetchData();
    } catch (err) {
      console.error('Failed to initialize month', err);
    } finally {
      setInitializing(false);
    }
  };

  const handleRecalculate = async () => {
    try {
      await apiClient.post('/api/budgets/recalculate_spending/', { month: monthStr });
      await fetchData();
    } catch (err) {
      console.error('Failed to recalculate', err);
    }
  };

  const hasCategories = data && data.groups.some(g => g.categories.length > 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16], paddingBottom: SPACING[64] }}>
      {/* Top bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <MonthSelector month={month} onChange={setMonth} />
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[12] }}>
          {data && (
            <div style={{
              fontFamily: TYPOGRAPHY.body.fontFamily,
              fontSize: TYPOGRAPHY.body.sizes.default,
              color: data.left_to_budget < 0 ? COLORS.status.error : COLORS.status.success,
              fontWeight: '600',
            }}>
              Left to budget: {formatCurrency(data.left_to_budget)}
            </div>
          )}
          <Button variant="ghost" onClick={handleRecalculate} disabled={loading}>
            Recalculate
          </Button>
          <Button variant="secondary" onClick={handleInitialize} disabled={initializing}>
            {initializing ? 'Initializing...' : 'Initialize Month'}
          </Button>
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: SPACING[32] }}>
          <Spinner size="md" />
        </div>
      ) : !hasCategories ? (
        <Panel style={{ textAlign: 'center', padding: SPACING[32] }}>
          <p style={{
            fontFamily: TYPOGRAPHY.body.fontFamily,
            fontSize: TYPOGRAPHY.body.sizes.default,
            color: COLORS.text.secondary,
            margin: 0,
          }}>
            No budget entries for this month. Click "Initialize Month" to create budget entries from your category configs.
          </p>
        </Panel>
      ) : (
        <>
          {/* Summary cards */}
          {data && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: SPACING[12] }}>
              <Panel>
                <div style={{ fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.secondary, marginBottom: SPACING[4] }}>Income</div>
                <div style={{ fontSize: TYPOGRAPHY.headings.sizes.h2, fontFamily: TYPOGRAPHY.headings.fontFamily, fontWeight: '700', color: COLORS.status.success }}>
                  {formatCurrency(data.total_income)}
                </div>
              </Panel>
              <Panel>
                <div style={{ fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.secondary, marginBottom: SPACING[4] }}>Expenses</div>
                <div style={{ fontSize: TYPOGRAPHY.headings.sizes.h2, fontFamily: TYPOGRAPHY.headings.fontFamily, fontWeight: '700', color: COLORS.status.error }}>
                  {formatCurrency(data.total_expenses)}
                </div>
              </Panel>
              <Panel>
                <div style={{ fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.secondary, marginBottom: SPACING[4] }}>Monthly Budget</div>
                <div style={{
                  fontSize: TYPOGRAPHY.headings.sizes.h2,
                  fontFamily: TYPOGRAPHY.headings.fontFamily,
                  fontWeight: '700',
                  color: COLORS.text.primary,
                }}>
                  {formatCurrency(data.monthly_budget_total ?? 0)}
                </div>
              </Panel>
            </div>
          )}

          {/* Budget groups */}
          {data?.groups.map((group) => (
            <BudgetGroupPanel key={group.group_name} group={group} />
          ))}
        </>
      )}
    </div>
  );
};

export default BudgetDashboard;
